import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import BMAuditExportButton from "./BMAuditExportButton";

function fmt(ts: string | null) {
  if (!ts) return "";
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function diffFields(before: any, after: any): string {
  if (!before && after) return Object.keys(after).filter(k => !["id","created_at","updated_at"].includes(k)).map(k => `${k}: ${JSON.stringify(after[k])}`).join(", ");
  if (before && !after) return "(deleted)";
  if (!before || !after) return "";
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const out: string[] = [];
  keys.forEach(k => {
    if (["id","created_at","updated_at"].includes(k)) return;
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) {
      out.push(`${k}: ${JSON.stringify(before[k])} → ${JSON.stringify(after[k])}`);
    }
  });
  return out.join(", ");
}

const PAGE_SIZE = 50;

export default function BMAuditTrail() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [table, setTable] = useState<string>("all");
  const [action, setAction] = useState<string>("all");
  const [user, setUser] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("bm-audit-export");
        if (error) throw error;
        setData(data);
      } catch (e: any) {
        toast.error(e?.message || "Failed to load audit trail");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const houseMap = useMemo(() => new Map<string, any>((data?.fundHouses || []).map((h: any) => [h.id, h])), [data]);
  const holdingMap = useMemo(() => new Map<string, any>((data?.holdings || []).map((h: any) => [h.id, h])), [data]);

  const enriched = useMemo(() => {
    const log = data?.auditLog || [];
    return log.map((a: any) => {
      const payload = a.after_data || a.before_data || {};
      let context = "";
      if (a.table_name === "bm_fund_houses") {
        const fh = houseMap.get(a.row_id) || payload;
        context = `${fh?.name || ""} (${fh?.benchmark ?? ""})`;
      } else if (a.table_name === "bm_fund_house_holdings") {
        const hld = holdingMap.get(a.row_id) || payload;
        const fh = hld?.fund_house_id ? houseMap.get(hld.fund_house_id) : null;
        context = `${fh?.name || ""} — ${hld?.holding_name || payload.holding_name || ""}`;
      } else if (a.table_name === "bm_holding_sector_mappings") {
        const hld = payload.holding_id ? holdingMap.get(payload.holding_id) : null;
        const fh = hld ? houseMap.get(hld.fund_house_id) : null;
        context = `${fh?.name || ""} — ${hld?.holding_name || ""} → ${payload.cbam_sector_name || ""}`;
      }
      return { ...a, context, diff: diffFields(a.before_data, a.after_data) };
    });
  }, [data, houseMap, holdingMap]);

  const users = useMemo(() => {
    const s = new Set<string>();
    enriched.forEach((a: any) => { if (a.user_email) s.add(a.user_email); });
    return Array.from(s).sort();
  }, [enriched]);

  const filtered = useMemo(() => {
    // Parse BOTH date bounds in UTC and include the full "To" day. Previously "From" parsed as UTC
    // midnight while "To" parsed in browser-local time, so end-of-day rows (e.g. 23:00–23:59Z during
    // UK summer / BST) were wrongly excluded.
    const fromMs = from ? Date.parse(`${from}T00:00:00.000Z`) : null;
    const toMs = to ? Date.parse(`${to}T23:59:59.999Z`) : null;
    return enriched.filter((a: any) => {
      if (table !== "all" && a.table_name !== table) return false;
      if (action !== "all" && a.action !== action) return false;
      if (user !== "all" && a.user_email !== user) return false;
      const t = Date.parse(a.changed_at);
      if (fromMs !== null && !isNaN(t) && t < fromMs) return false;
      if (toMs !== null && !isNaN(t) && t > toMs) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = `${a.context} ${a.diff} ${a.user_email || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [enriched, table, action, user, from, to, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => { setPage(0); }, [table, action, user, from, to, search]);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const tableLabel = (t: string) => ({
    bm_fund_houses: "Fund House",
    bm_fund_house_holdings: "Holdings",
    bm_holding_sector_mappings: "Sector Mapping",
  } as Record<string, string>)[t] || t;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Audit Trail</h2>
          <p className="text-sm text-muted-foreground">All changes to fund houses, holdings, and sector mappings (Super Admin only).</p>
        </div>
        <BMAuditExportButton />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Select value={table} onValueChange={setTable}>
          <SelectTrigger><SelectValue placeholder="Table" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tables</SelectItem>
            <SelectItem value="bm_fund_houses">Fund Houses</SelectItem>
            <SelectItem value="bm_fund_house_holdings">Holdings</SelectItem>
            <SelectItem value="bm_holding_sector_mappings">Sector Mappings</SelectItem>
          </SelectContent>
        </Select>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="INSERT">Insert</SelectItem>
            <SelectItem value="UPDATE">Update</SelectItem>
            <SelectItem value="DELETE">Delete</SelectItem>
          </SelectContent>
        </Select>
        <Select value={user} onValueChange={setUser}>
          <SelectTrigger><SelectValue placeholder="User" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {users.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={from} onChange={e => setFrom(e.target.value)} placeholder="From" />
        <Input type="date" value={to} onChange={e => setTo(e.target.value)} placeholder="To" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
      </div>

      <div className="text-xs text-muted-foreground">
        {filtered.length} record{filtered.length === 1 ? "" : "s"} — page {page + 1} of {pageCount}
      </div>

      <div className="border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="px-3 py-2 whitespace-nowrap">Timestamp</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Table</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Context</th>
              <th className="px-3 py-2">Change</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No entries</td></tr>
            ) : pageRows.map((a: any, i: number) => (
              <tr key={`${a.changed_at}-${i}`} className="border-t align-top">
                <td className="px-3 py-2 whitespace-nowrap">{fmt(a.changed_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{a.user_email || <span className="text-muted-foreground">(system)</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap">{tableLabel(a.table_name)}</td>
                <td className="px-3 py-2"><span className={
                  a.action === "INSERT" ? "text-green-600" : a.action === "DELETE" ? "text-red-600" : "text-amber-600"
                }>{a.action}</span></td>
                <td className="px-3 py-2">{a.context}</td>
                <td className="px-3 py-2 text-xs font-mono break-all">{a.diff}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
          <ChevronLeft className="h-4 w-4" /> Prev
        </Button>
        <Button size="sm" variant="outline" disabled={page >= pageCount - 1} onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}>
          Next <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
